/**
 * video-hero.js
 * Shopify Horizon Teması — Video Hero Section Manager
 * assets/ klasörüne yükleyin
 */

class VideoHeroManager {
  static instances = new Map();

  static init(sectionId) {
    const section = document.querySelector(`[data-video-hero-section="${sectionId}"]`);
    if (!section) return;

    // Zaten varsa yenile
    this.destroy(sectionId);

    const instance = new VideoHeroManager(section, sectionId);
    this.instances.set(sectionId, instance);
    instance.init();
  }

  static destroy(sectionId) {
    const instance = this.instances.get(sectionId);
    if (instance) {
      instance.destroy();
      this.instances.delete(sectionId);
    }
  }

  constructor(section, sectionId) {
    this.section    = section;
    this.sectionId  = sectionId;
    this.el         = {};
    this._handlers  = [];
    this._timers    = [];
    this.isMuted    = true;
    this.isPaused   = false;
  }

  /* ------------------------------------------ */
  /* Init                                         */
  /* ------------------------------------------ */
  init() {
    this._cacheElements();
    this._applyMobileHeight();
    this._setupVideo();
    this._setupControls();
    this._setupReducedMotion();
  }

  /* ------------------------------------------ */
  /* Cache DOM                                    */
  /* ------------------------------------------ */
  _cacheElements() {
    const q = (sel) => this.section.querySelector(sel);
    this.el = {
      container : q('.video-hero__container'),
      video     : q('video.video-hero__video'),
      iframe    : q('iframe.video-hero__video'),
      loading   : q('.video-hero__loading'),
      muteBtn   : q('[data-mute-toggle]'),
      playBtn   : q('[data-play-toggle]'),
    };
  }

  /* ------------------------------------------ */
  /* Mobile Height                                */
  /* ------------------------------------------ */
  _applyMobileHeight() {
    const val = this.section.dataset.mobileHeight;
    if (val && this.el.container) {
      this.el.container.classList.add(`video-hero--mobile-${val}`);
    }
  }

  /* ------------------------------------------ */
  /* Video Setup                                  */
  /* ------------------------------------------ */
  _setupVideo() {
    this._showLoading();

    if (this.el.video) {
      this._setupHTML5Video();
    } else if (this.el.iframe) {
      this._setupIframe();
    } else {
      // Görsel ya da boş — loadingi gizle
      this._hideLoading();
    }

    // Güvenlik timeri: 4 saniye sonra mutlaka kapat
    this._addTimer(setTimeout(() => this._hideLoading(), 4000));
  }

  _setupHTML5Video() {
    const v = this.el.video;

    // Autoplay + loop garantisi: attribute'ları JS ile de set et
    v.autoplay    = true;
    v.loop        = true;
    v.muted       = true;
    v.playsInline = true;

    // Yüklendi eventleri → loading kapat
    const readyEvents = ['loadeddata', 'canplay', 'canplaythrough'];
    readyEvents.forEach((ev) => {
      this._addHandler(v, ev, () => this._hideLoading(), { once: true });
    });

    // Hata durumunda da loading kapat
    ['error', 'abort', 'stalled'].forEach((ev) => {
      this._addHandler(v, ev, () => this._hideLoading(), { once: true });
    });

    // Video bitince başa al (loop attribute güvencesi)
    this._addHandler(v, 'ended', () => {
      v.currentTime = 0;
      v.play().catch(() => {});
    });

    // Zaten yüklenmiş mi kontrol et
    if (v.readyState >= 2) {
      this._hideLoading();
      this._tryAutoplay(v);
      return;
    }

    // Autoplay denemesi
    this._tryAutoplay(v);
  }

  _setupIframe() {
    this._addHandler(this.el.iframe, 'load', () => this._hideLoading(), { once: true });
  }

  _tryAutoplay(video) {
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Autoplay engellendiyse sessizce geç
      });
    }
  }

  /* ------------------------------------------ */
  /* Controls                                     */
  /* ------------------------------------------ */
  _setupControls() {
    const { video, muteBtn, playBtn } = this.el;
    if (!video) return;

    if (muteBtn) {
      this._addHandler(muteBtn, 'click', () => this._toggleMute());
      this._updateMuteIcon();
    }

    if (playBtn) {
      this._addHandler(playBtn, 'click', () => this._togglePlay());

      // Video durumuna göre ikonu güncelle
      this._addHandler(video, 'play',  () => this._updatePlayIcon(false));
      this._addHandler(video, 'pause', () => this._updatePlayIcon(true));
    }
  }

  _toggleMute() {
    const { video, muteBtn } = this.el;
    if (!video) return;
    this.isMuted  = !this.isMuted;
    video.muted   = this.isMuted;
    muteBtn?.setAttribute('aria-pressed', String(!this.isMuted));
    this._updateMuteIcon();
  }

  _togglePlay() {
    const { video } = this.el;
    if (!video) return;

    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  _updateMuteIcon() {
    const btn = this.el.muteBtn;
    if (!btn) return;

    // Ses açık ikonu
    const iconOn = `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
    </svg>`;

    // Ses kapalı ikonu
    const iconOff = `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.5 12A4.5 4.5 0 0014 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 003.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
    </svg>`;

    btn.innerHTML = this.isMuted ? iconOff : iconOn;
    btn.setAttribute('aria-label', this.isMuted ? 'Sesi Aç' : 'Sesi Kapat');
  }

  _updatePlayIcon(showPlay) {
    const btn = this.el.playBtn;
    if (!btn) return;

    const iconPlay = `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5v14l11-7z"/>
    </svg>`;

    const iconPause = `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
    </svg>`;

    btn.innerHTML = showPlay ? iconPlay : iconPause;
    btn.setAttribute('aria-label', showPlay ? 'Oynat' : 'Durdur');
    btn.setAttribute('aria-pressed', String(!showPlay));
  }

  /* ------------------------------------------ */
  /* Reduced Motion (accessibility)              */
  /* ------------------------------------------ */
  _setupReducedMotion() {
    // Hero video'ları her zaman çalar — prefers-reduced-motion sadece
    // CSS animasyonlarını etkiler, video autoplay'i durdurmayız.
    // (CSS animasyonları zaten @keyframes ile tanımlandığı için
    //  tarayıcı kendi media query'i ile onları zaten durdurabilir.)
  }

  /* ------------------------------------------ */
  /* Loading State                                */
  /* ------------------------------------------ */
  _showLoading() {
    this.el.loading?.classList.remove('video-hero__loading--hidden');
  }

  _hideLoading() {
    this.el.loading?.classList.add('video-hero__loading--hidden');
  }

  /* ------------------------------------------ */
  /* Event / Timer Management                    */
  /* ------------------------------------------ */
  _addHandler(target, event, fn, options) {
    if (!target || !target.addEventListener) return;
    target.addEventListener(event, fn, options);
    this._handlers.push({ target, event, fn });
  }

  _addTimer(id) {
    this._timers.push(id);
  }

  /* ------------------------------------------ */
  /* Destroy                                      */
  /* ------------------------------------------ */
  destroy() {
    this._handlers.forEach(({ target, event, fn }) => {
      target?.removeEventListener(event, fn);
    });
    this._timers.forEach((id) => clearTimeout(id));
    this._handlers = [];
    this._timers   = [];
    this.el        = {};
  }
}

/* -------------------------------------------------- */
/* Global Export                                       */
/* -------------------------------------------------- */
window.VideoHeroManager = VideoHeroManager;

/* -------------------------------------------------- */
/* Auto-init (non-editor)                              */
/* -------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  if (window.Shopify?.designMode) return; // Editor bunu ayrıca çalıştırır

  document.querySelectorAll('[data-video-hero-section]').forEach((el) => {
    const id = el.dataset.videoHeroSection;
    if (id) VideoHeroManager.init(id);
  });
});
